/* The two-question finder. Config JSON lives in a <script data-finder-config> inside [data-finder]:
   { name, kicker, title, collection, steps:[{key,q,opts:[[v,label]],skip_if:{key:[v]}}], rules:[{if:{k:v},r:'id'}],
     products:{ id:{ handle|l, n, tier, w, price? } }, packkey, packs:{ v:'html', _:'html' } }
   or keyed by category { knees:{…} }, or multi-category { _multi:true, _catq:'…', _cats:[[id,label]], <id>:{…}, … }.
   A pick referenced by handle is shown as a product card fetched from /products/{handle}?section_id=finder-card
   (the finder-card section: photos, name, price, Quick add, Choose pack, links to the product); the text result,
   with its live price from /products/{handle}.js, is the fallback when there is no handle or the fetch fails. */
import { $$, money, track, onReady } from '@theme/utils';

const T = () => window.Theme || { settings: {}, strings: {} };
const priceCache = {};
const cardCache = {};

async function fetchCard(handle, root) {
  if (cardCache[handle] !== undefined) return cardCache[handle];
  try {
    const res = await fetch(`${root}products/${handle}?section_id=finder-card`, { headers: { Accept: 'text/html' } });
    if (!res.ok) throw new Error('no card');
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    const card = doc.querySelector('.card');
    cardCache[handle] = card ? card.outerHTML : '';
  } catch (e) { cardCache[handle] = ''; }
  return cardCache[handle];
}

async function livePrice(handle, root) {
  if (priceCache[handle]) return priceCache[handle];
  try {
    const res = await fetch(`${root}products/${handle}.js`);
    if (!res.ok) throw new Error('no product');
    const p = await res.json();
    const pn = T().settings.packOptionNames || 'quantity,pack';
    const packNames = (Array.isArray(pn) ? pn : String(pn).split(',')).map((x) => String(x).trim().toLowerCase());
    const pi = (p.options || []).findIndex((o) => packNames.includes(String(o.name || o).trim().toLowerCase()));
    const vars = p.variants || [];
    const qtyOf = (v) => { if (pi < 0) return 1; const m = /^\s*(\d+)/.exec(v.options[pi] || ''); return m ? parseInt(m[1], 10) : 1; };
    const singles = vars.filter((v) => qtyOf(v) === 1), twos = vars.filter((v) => qtyOf(v) === 2);
    const one = singles.length ? Math.min(...singles.map((v) => v.price)) : Math.min(...vars.map((v) => v.price));
    let line = money(one);
    if (twos.length) { const two = Math.min(...twos.map((v) => v.price)); line += ` · ${T().strings.twoPackEach ? T().strings.twoPackEach.replace('[price]', money(two / 2)) : '2-pack ' + money(two / 2) + ' each'}`; }
    priceCache[handle] = { line, url: p.url || `${root}products/${handle}` };
  } catch (e) { priceCache[handle] = { line: '', url: `${root}products/${handle}` }; }
  return priceCache[handle];
}

function init(f) {
  if (f.__finder) return; f.__finder = true;
  let cfgs; try { cfgs = JSON.parse(f.querySelector('[data-finder-config]').textContent); } catch (e) { return; }
  if (cfgs && Array.isArray(cfgs.steps)) cfgs = { single: cfgs }; /* a flat config = one category */
  const root = f.dataset.root || '/';
  const multi = !!cfgs._multi; let cat = multi ? null : Object.keys(cfgs).filter((k) => !k.startsWith('_'))[0]; let ans = {};
  const qs = f.querySelector('[data-fq]'), res = f.querySelector('[data-fres]');
  const cfg = () => (cat ? cfgs[cat] : null);
  const head = cfg(); if (head && !multi) { const k = f.querySelector('[data-finder-kicker]'), t = f.querySelector('[data-finder-title]'); if (k && head.kicker) k.textContent = head.kicker; if (t && head.title) t.textContent = head.title; }
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const chips = (key, opts, sel) => '<div class="chips" role="group">' + opts.map((o) => `<button type="button" class="chip${sel === o[0] ? ' on' : ''}" aria-pressed="${sel === o[0]}" data-k="${esc(key)}" data-v="${esc(o[0])}">${esc(o[1])}</button>`).join('') + '</div>';
  const skip = (st) => !!st.skip_if && Object.keys(st.skip_if).some((k) => st.skip_if[k].indexOf(ans[k]) > -1);
  function render() {
    let h = '', n = 1;
    if (multi) { h += `<div class="fq"><p class="lab" style="margin-bottom:8px">${n} · ${esc(cfgs._catq)}</p>${chips('_cat', cfgs._cats, cat)}</div>`; n++; }
    const c = cfg();
    if (c) {
      let shown = 0; const live = c.steps.filter((x) => !skip(x));
      c.steps.forEach((st) => {
        if (skip(st)) return;
        const prev = live[shown - 1];
        if (shown > 0 && prev && ans[prev.key] === undefined) return;
        h += `<div class="fq"><p class="lab" style="margin:${n > 1 ? '14px' : '0'} 0 8px">${n} · ${esc(st.q)}</p>${chips(st.key, st.opts, ans[st.key])}</div>`; n++; shown++;
      });
    }
    qs.innerHTML = h; result();
  }
  const done = () => { const c = cfg(); return !!c && c.steps.every((st) => skip(st) || ans[st.key] !== undefined); };
  const slot = res.querySelector('[data-fres-card]'); let seq = 0;
  const showCard = (html) => {
    if (!slot) return;
    if (!html) { slot.hidden = true; slot.innerHTML = ''; res.classList.remove('has-card'); return; }
    slot.innerHTML = html; slot.hidden = false; res.classList.add('has-card');
    /* the card's galleries, quick add and view events are wired by the global script */
    document.dispatchEvent(new CustomEvent('nocry:section:rendered', { detail: { root: slot } }));
  };
  async function result() {
    const c = cfg(); if (!c || !done()) { res.hidden = true; return; }
    const hit = c.rules.filter((r) => Object.keys(r.if).every((k) => ans[k] === r.if[k]))[0];
    if (!hit) { res.hidden = true; return; }
    const p = c.products[hit.r]; if (!p) { res.hidden = true; return; }
    const token = ++seq;
    /* the card: straight from the cache when this pick was shown before, else after the fetch (text result meanwhile) */
    const cached = p.handle ? cardCache[p.handle] : '';
    showCard(cached || '');
    res.querySelector('[data-fres-name]').textContent = p.n || '';
    const tier = res.querySelector('[data-fres-tier]'); tier.textContent = p.tier || ''; tier.className = 'tag ' + String(p.tier || '').toLowerCase(); tier.hidden = !p.tier;
    res.querySelector('[data-fres-why]').textContent = p.w || '';
    const priceEl = res.querySelector('[data-fres-price]'); priceEl.textContent = p.price || '';
    const lk = res.querySelector('[data-fres-link]');
    let url = p.l || (p.handle ? `${root}products/${p.handle}` : '#');
    lk.href = url; lk.textContent = T().strings.choosePack || 'Choose pack';
    const all = res.querySelector('[data-fres-all]'); if (c.collection) all.href = c.collection; all.textContent = (T().strings.allOf || 'All [name]').replace('[name]', (c.name || '').toLowerCase());
    res.querySelector('[data-fres-pack]').innerHTML = (c.packs && (c.packs[ans[c.packkey]] || c.packs._)) || '';
    res.hidden = false; res.scrollIntoView({ block: 'nearest' });
    track('finder_result', { finder: cat, pick: hit.r, answers: JSON.stringify(ans) });
    if (p.handle && cached === undefined) { const html = await fetchCard(p.handle, root); if (token === seq) showCard(html); }
    if (p.handle && !p.price) { const lp = await livePrice(p.handle, root); if (token !== seq) return; if (lp.line) priceEl.textContent = lp.line; if (lp.url && !p.l) lk.href = lp.url; }
  }
  f.addEventListener('click', (e) => {
    const b = e.target.closest('[data-k]');
    if (b) { if (b.dataset.k === '_cat') { cat = b.dataset.v; ans = {}; } else ans[b.dataset.k] = b.dataset.v; render(); }
    if (e.target.closest('[data-fres-reset]')) { ans = {}; if (multi) cat = null; render(); }
    const t = e.target.closest('.ftoggle');
    if (t) { f.classList.toggle('collapsed'); t.setAttribute('aria-expanded', String(!f.classList.contains('collapsed'))); }
  });
  const openIfHash = () => { if (location.hash === '#finder' && f.classList.contains('collapsed')) { f.classList.remove('collapsed'); const t = f.querySelector('.ftoggle'); if (t) t.setAttribute('aria-expanded', 'true'); } };
  openIfHash(); addEventListener('hashchange', openIfHash);
  document.addEventListener('click', (e) => { if (e.target.closest('a[href$="#finder"]')) { f.classList.remove('collapsed'); const t = f.querySelector('.ftoggle'); if (t) t.setAttribute('aria-expanded', 'true'); } });
  render();
}

export function initFinders(root = document) { $$('[data-finder]', root).forEach(init); }
onReady(() => initFinders());
document.addEventListener('shopify:section:load', (e) => initFinders(e.target));
document.addEventListener('nocry:section:rendered', (e) => initFinders((e.detail && e.detail.root) || document));
window.NOCRY = Object.assign(window.NOCRY || {}, { initFinders });
